import React, { useState, useEffect, useRef } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./EditAppointmentModal.module.css";
import { IAppointment } from "../../types/appointments.types";
import {
  useValidateAppointmentChangeMutation,
  usePatchAppointmentMutation,
} from "../../../../services/api/utils.api";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import { timeToMinutes } from "../../../../utils/utils";
import SeriesManager from "../SeriesManager/SeriesManager";

interface IEditAppointmentModalProps {
  appointment: IAppointment;
  onClose: () => void;
  onSuccess: () => void;
  isEmbedded?: boolean;
}

interface IConflict {
  type: string;
  message: string;
  severity: 'warning' | 'info';
}

interface ITherapistAvailability {
  dayId: number;
  startTime: string;
  endTime: string;
}

/**
 * Edit Appointment Modal Component
 *
 * Allows editing appointment details with validation:
 * - Date and time
 * - Therapist
 * - Media/Method
 * - Notes/Comments
 * 
 * Note: Changes apply only to the specific appointment, not the entire treatment program.
 */
const EditAppointmentModal: React.FC<IEditAppointmentModalProps> = ({
  appointment,
  onClose,
  onSuccess,
  isEmbedded = false,
}) => {
  const isRtl = i18n.language === "he";

  // Form state
  const [date, setDate] = useState(appointment.date);
  const [startTime, setStartTime] = useState(appointment.time || "");
  const [endTime, setEndTime] = useState(appointment.endTime || "");
  const [therapistId, setTherapistId] = useState(appointment.therapistsId || 0);
  const [mediaId, setMediaId] = useState(appointment.mediaId || 0);
  const [comments, setComments] = useState(appointment.comments || "");

  // Update state when appointment prop changes
  useEffect(() => {
    setDate(appointment.date);
    setStartTime(appointment.time || "");
    setEndTime(appointment.endTime || "");
    setTherapistId(appointment.therapistsId || 0);
    setMediaId(appointment.mediaId || 0);
    setComments(appointment.comments || "");
  }, [appointment]);

  // Edit mode states
  const [isEditingTherapist, setIsEditingTherapist] = useState(false);
  const [showTherapistDropdown, setShowTherapistDropdown] = useState(false);
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);
  const therapistDropdownRef = useRef<HTMLDivElement>(null);
  const mediaDropdownRef = useRef<HTMLDivElement>(null);

  // Validation state
  const [conflicts, setConflicts] = useState<IConflict[]>([]);
  const [therapistAvailability, setTherapistAvailability] = useState<ITherapistAvailability[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // Get therapists from context (already loaded)
  const { therapistsList } = useAppointmentsContext();

  // API hooks
  const [validateChange] = useValidateAppointmentChangeMutation();
  const [patchAppointment, { isLoading: isPatchingAppointment }] = usePatchAppointmentMutation();

  const isOther = !appointment.usersId && !appointment.treatmentGroupId && appointment.source !== "google_calendar";

  // Media types - hardcoded for now, should come from API
  const mediaTypes = [
    { id: 1, name: t("MEDIA_CHAT") },
    { id: 2, name: t("MEDIA_VIDEO") },
    { id: 3, name: t("MEDIA_PHONE") },
    { id: 4, name: t("MEDIA_CLINIC") },
  ];

  // Validate changes when relevant fields change
  useEffect(() => {
    const validateChanges = async () => {
      // Skip validation for "Other" events (no patient attached)
      if (isOther) return;

      setIsValidating(true);
      try {
        const therapistsGuidForValidate =
          therapistId === appointment.therapistsId
            ? appointment.therapistsGuid
            : therapistsList.find((t) => t.id === therapistId)?.guid;

        const result = await validateChange({
          appointmentId: appointment.id,
          therapistsGuid: therapistsGuidForValidate,
          usersGuid: appointment.usersGuid,
          date: date || appointment.date,
          startTime: startTime || appointment.time,
          endTime: endTime || appointment.endTime,
        }).unwrap();

        setConflicts(result.conflicts || []);
        setTherapistAvailability(result.therapistAvailability || []);
      } catch (error) {
        console.error("Validation error:", error);
      } finally {
        setIsValidating(false);
      }
    };

    // Only validate if something changed
    const hasChanges = 
      date !== appointment.date ||
      startTime !== appointment.time ||
      endTime !== appointment.endTime ||
      therapistId !== appointment.therapistsId;

    if (hasChanges && !isOther) {
      validateChanges();
    } else {
      setConflicts([]);
      setTherapistAvailability([]);
    }
  }, [date, startTime, endTime, therapistId, appointment, therapistsList, validateChange, isOther]);

  const isStartBeforeEnd = timeToMinutes(startTime) < timeToMinutes(endTime);
  const handleSave = async () => {
    try {
      // Check what fields changed
      const hasChanges = 
        date !== appointment.date ||
        startTime !== appointment.time ||
        endTime !== appointment.endTime ||
        therapistId !== appointment.therapistsId ||
        mediaId !== appointment.mediaId ||
        comments !== (appointment.comments || "");

      if (hasChanges) {
        // Find therapist GUID if therapist changed
        let therapistsGuid: string | undefined = undefined;
        if (therapistId !== appointment.therapistsId && therapistId > 0) {
          const selectedTherapist = therapistsList.find(t => t.id === therapistId);
          therapistsGuid = selectedTherapist?.guid;
        }

        // Update only the specific appointment (not the program)
        await patchAppointment({
          id: appointment.id,
          appointmentDate: date !== appointment.date ? date : undefined,
          startTime: startTime !== appointment.time ? startTime : undefined,
          endTime: endTime !== appointment.endTime ? endTime : undefined,
          therapistsId: therapistId !== appointment.therapistsId ? therapistId : undefined,
          therapistsGuid,
          mediaId: mediaId !== appointment.mediaId ? mediaId : undefined,
          comments: comments !== (appointment.comments || "") ? comments : undefined,
        }).unwrap();
      }
      onSuccess();
    } catch (error) {
      console.error("Failed to update appointment:", error);
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  };

  const getDayName = (dayId: number): string => {
    const days = [
      t("SUNDAY"),
      t("MONDAY"),
      t("TUESDAY"),
      t("WEDNESDAY"),
      t("THURSDAY"),
      t("FRIDAY"),
      t("SATURDAY"),
    ];
    return days[dayId - 1] || "";
  };

  const currentTherapistId = therapistId || appointment.therapistsId || 0;
  const therapists = therapistsList
    .filter((t) => t.isActive || t.id === currentTherapistId)
    .map((t) => ({
      id: t.id,
      fullName: t.name,
      isActive: t.isActive,
    }));

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (therapistDropdownRef.current && !therapistDropdownRef.current.contains(event.target as Node)) {
        setShowTherapistDropdown(false);
      }
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target as Node)) {
        setShowMediaDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const editFormContent = (
    <>
      <div className={styles.modalHeader}>
        <div className={styles.modalTitle}>
          <span>✏️</span>
          <span>{t("APPOINTMENTS_EDIT_TITLE")}</span>
        </div>
        <button className={styles.closeBtn} onClick={onClose}>
          ×
        </button>
      </div>

      <div className={styles.modalBody}>

          {/* Series Manager — shown at top when appointment belongs to a repeating series */}
          {appointment.seriesId && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>🔁</span>
                {t("SERIES_MANAGEMENT") || "סדרת פגישות"}
              </label>
              <SeriesManager
                seriesId={appointment.seriesId}
                currentAppointmentId={appointment.id}
                onSuccess={onSuccess}
              />
            </div>
          )}

          {/* Date Field */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📅</span>
              {t("APPOINTMENTS_DATE")}
            </label>
            <input
              type="date"
              className={styles.input}
              value={formatDate(date)}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {/* Time Fields */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>⏰</span>
                {t("APPOINTMENTS_START_TIME")}
              </label>
              <input
                type="time"
                className={styles.input}
                style={(!isStartBeforeEnd) ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                {t("APPOINTMENTS_END_TIME")}
              </label>
              <input
                type="time"
                className={styles.input}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
            {!isStartBeforeEnd && startTime && endTime && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '-10px', marginBottom: '10px' }}>
                  {t("START_TIME_BEFORE_END_TIME_ERROR") || "שעת הסיום חייבת להיות אחרי שעת ההתחלה"}
              </div>
          )}
          </div>

          {/* Therapist Field - Only for regular appointments */}
          {!isOther && (
            <div className={styles.formGroup} ref={therapistDropdownRef}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>👨‍⚕️</span>
                {t("APPOINTMENTS_THERAPIST")}
              </label>
              {!isEditingTherapist ? (
                <div className={styles.currentValueWrapper}>
                  <div className={styles.currentValue}>
                    👨‍⚕️ {appointment.therapistName || t("NO_THERAPIST")}
                  </div>
                  <button
                    type="button"
                    className={styles.changeButton}
                    onClick={() => {
                      setIsEditingTherapist(true);
                      setShowTherapistDropdown(true);
                    }}
                  >
                    {t("CHANGE")}
                  </button>
                </div>
              ) : (
                <div className={styles.selectWrapper}>
                  <div className={styles.customSelect}>
                    <div
                      className={`${styles.customSelectTrigger} ${showTherapistDropdown ? styles.customSelectActive : ''}`}
                      onClick={() => setShowTherapistDropdown(!showTherapistDropdown)}
                    >
                      <span className={styles.customSelectValue}>
                        👨‍⚕️ {therapists.find(t => t.id === therapistId)?.fullName || t("SELECT_THERAPIST")}
                      </span>
                      <span className={styles.customSelectArrow}>▼</span>
                    </div>
                    {showTherapistDropdown && (
                      <div className={styles.customSelectDropdown}>
                        <div className={styles.dropdownOptions}>
                          {therapists.length === 0 ? (
                            <div className={styles.dropdownOption}>
                              <span className={styles.optionText}>{t("LOADING")}...</span>
                            </div>
                          ) : (
                            therapists.map((th: any) => (
                              <div
                                key={th.id}
                                className={`${styles.dropdownOption} ${therapistId === th.id ? styles.dropdownOptionSelected : ''}`}
                                onClick={() => {
                                  setTherapistId(th.id);
                                  setShowTherapistDropdown(false);
                                }}
                              >
                                <span className={styles.optionText}>👨‍⚕️ {th.fullName}</span>
                                {therapistId === th.id && <span className={styles.checkmark}>✓</span>}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => {
                      setIsEditingTherapist(false);
                      setShowTherapistDropdown(false);
                      setTherapistId(appointment.therapistsId || 0);
                    }}
                  >
                    {t("CANCEL")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Media Field - Only for regular appointments */}
          {appointment.usersId || appointment.treatmentGroupId ? (
            <div className={styles.formGroup} ref={mediaDropdownRef}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>📍</span>
                {t("APPOINTMENTS_MEDIA")}
              </label>
              <div className={styles.customSelect}>
                <div
                  className={`${styles.customSelectTrigger} ${showMediaDropdown ? styles.customSelectActive : ''}`}
                  onClick={() => setShowMediaDropdown(!showMediaDropdown)}
                >
                  <span className={styles.customSelectValue}>
                    {mediaTypes.find(m => m.id === mediaId)?.name || t("SELECT_MEDIA")}
                  </span>
                  <span className={styles.customSelectArrow}>▼</span>
                </div>
                {showMediaDropdown && (
                  <div className={styles.customSelectDropdown}>
                    <div className={styles.dropdownOptions}>
                      {mediaTypes.map((media) => (
                        <div
                          key={media.id}
                          className={`${styles.dropdownOption} ${mediaId === media.id ? styles.dropdownOptionSelected : ''}`}
                          onClick={() => {
                            setMediaId(media.id);
                            setShowMediaDropdown(false);
                          }}
                        >
                          <span className={styles.optionText}>{media.name}</span>
                          {mediaId === media.id && <span className={styles.checkmark}>✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}


          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📝</span>
              {isOther ? (t("MESSAGE_CONTENT") || "תוכן ההודעה") : t("APPOINTMENTS_NOTES")}
            </label>
            <textarea
              className={styles.textarea}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder={t("APPOINTMENTS_NOTES_PLACEHOLDER")}
            />
          </div>

          {/* Warnings Section */}
          {conflicts.length > 0 && (
            <div className={styles.warningsSection}>
              <div className={styles.warningsTitle}>
                <span>⚠️</span>
                {t("APPOINTMENTS_WARNINGS")}
              </div>
              <div className={styles.warningsList}>
                {conflicts.map((conflict, index) => (
                  <div
                    key={index}
                    className={`${styles.warningItem} ${
                      conflict.severity === 'warning' ? styles.warningHigh : styles.warningLow
                    }`}
                  >
                    <span className={styles.warningIcon}>
                      {conflict.severity === 'warning' ? '⚠️' : 'ℹ️'}
                    </span>
                    <span>{conflict.message}</span>
                    {conflict.type === 'therapist_not_available' && therapistAvailability.length > 0 && (
                      <div className={styles.availabilityTooltip}>
                        <span className={styles.availabilityLabel}>
                          📅 {t("THERAPIST_AVAILABILITY")}:
                        </span>
                        <div className={styles.availabilityList}>
                          {therapistAvailability.map((av, idx) => (
                            <div key={idx} className={styles.availabilityItem}>
                              {getDayName(av.dayId)}: {av.startTime} - {av.endTime}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.warningNote}>
                {t("APPOINTMENTS_CAN_OVERRIDE")}
              </div>
            </div>
          )}
      </div>

      <div className={styles.modalFooter}>
        <button className={styles.cancelBtn} onClick={onClose}>
          {t("APPOINTMENTS_CLOSE")}
        </button>
        <button
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={isPatchingAppointment || !isStartBeforeEnd || isValidating}
        >
          {isPatchingAppointment ? t("SAVING") : t("APPOINTMENTS_SAVE")}
        </button>
      </div>
    </>
  );

  if (isEmbedded) {
    return (
      <div className={styles.embeddedWrapper} dir={isRtl ? "rtl" : "ltr"}>
        {editFormContent}
      </div>
    );
  }

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal} dir={isRtl ? "rtl" : "ltr"}>
        {editFormContent}
      </div>
    </div>
  );
};

export default EditAppointmentModal;
